// ADV-1018: "What happened?" panel for an import BATCH (set of child jobs
// sharing core.import_jobs.batch_id). Mounts on the /import dashboard for any
// recent batch ending in errors and inline in the restore-from-backup flow
// (Phase 4). Mirrors the per-job DiagnosisPanel (ADV-1157) structure but adds:
//
//   - Collapsible Card with title "What happened?"
//   - Cluster pills (data | platform | config | transient) sorted by count
//   - Ladder breadcrumb derived from blocking_tier + IMPORT_TIER_ORDER
//   - Deep-link "Show records with errors" remediation
//
// Tokens only (ADR-0018); copy is plain-language second-person (ADV-1165 user
// register).

import { useEffect, useState } from "react";
import { ChevronDown, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import type {
  BatchDiagnosis,
  ImportDiagnosis,
  ImportDiagnosisCategory,
  ImportDiagnosisCluster,
  ImportEntityType,
} from "@adva/types";
import { IMPORT_TIER_ORDER } from "@adva/types";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAbility } from "@/hooks/use-ability";
import { useDiagnoseBatch } from "@/hooks/use-import";
import { DiagnosisResult } from "../jobs/components/diagnosis-result";

const CATEGORY_LABEL: Record<ImportDiagnosisCategory, string> = {
  data: "Data issue",
  platform: "Logged with Adva",
  config: "Import setup issue",
  transient: "Temporary issue",
};

const CATEGORY_TOKEN: Record<ImportDiagnosisCategory, string> = {
  data: "var(--color-warning)",
  platform: "var(--color-text-md)",
  config: "var(--color-accent)",
  transient: "var(--color-text-md)",
};

/** Reverse lookup: tier number → list of entity types at that tier. Built once
 * from IMPORT_TIER_ORDER so the ladder breadcrumb shows the right entity
 * family for a blocking tier without hard-coding it. */
function entityTypesAtTier(tier: number): ImportEntityType[] {
  return (
    Object.entries(IMPORT_TIER_ORDER) as Array<[ImportEntityType, number]>
  )
    .filter(([, t]) => t === tier)
    .map(([entity]) => entity);
}

/** Render "tier 2 customer → tier 3 opportunity → tier 4 proposal" when the
 * blocker is on tier 2. Cap at 3 tiers downstream so the breadcrumb stays
 * scannable. */
function LadderBreadcrumb({ blockingTier }: { blockingTier: number | null }) {
  if (blockingTier == null) return null;
  const downstreamTiers = [blockingTier, blockingTier + 1, blockingTier + 2];
  const labels = downstreamTiers
    .map((t) => {
      const entities = entityTypesAtTier(t);
      // Pick the canonical "headline" entity for the tier when there are many
      // — first alphabetical, since IMPORT_TIER_ORDER doesn't pin one.
      const head = entities.slice().sort()[0];
      if (!head) return null;
      return `tier ${t} ${head}`;
    })
    .filter((s): s is string => s !== null);
  if (labels.length === 0) return null;
  return (
    <p className="text-xs text-[color:var(--color-text-md)]">
      {labels.join(" → ")}
    </p>
  );
}

function ClusterPills({ clusters }: { clusters: ImportDiagnosisCluster[] }) {
  const sorted = [...clusters].sort((a, b) => b.count - a.count);
  return (
    <div className="flex flex-wrap gap-2">
      {sorted.map((c) => (
        <Badge
          key={c.category}
          variant="outline"
          style={{
            color: CATEGORY_TOKEN[c.category],
            borderColor: CATEGORY_TOKEN[c.category],
          }}
        >
          {CATEGORY_LABEL[c.category]} · {c.count}
        </Badge>
      ))}
    </div>
  );
}

/** Adapt a BatchDiagnosis to the ImportDiagnosis shape DiagnosisResult
 * consumes. The per-job component already handles operator/user register split,
 * USER_SAFE_KINDS filtering, and the model_metadata footer — reuse it instead
 * of duplicating 200 lines of presentation. */
function asImportDiagnosis(b: BatchDiagnosis): ImportDiagnosis {
  return {
    // The per-job component keys on `job_id`; for a batch we surface the
    // oldest child job id so any operator who copies the field gets something
    // useful. Falls back to the batch id when no children exist (defensive —
    // the service already 404s in that case).
    job_id: b.child_job_ids[0] ?? b.batch_id,
    root_cause: b.root_cause,
    evidence: b.evidence,
    suggested_remediation: b.suggested_remediation,
    retry_safe: b.retry_safe,
    file_linear_ticket: b.file_linear_ticket,
    model_metadata: b.model_metadata,
  };
}

interface BatchDiagnosisPanelProps {
  batchId: string;
  /** When true (used by the restore-from-backup inline mount), fire the
   * diagnose mutation on first render. When false (dashboard mount), require
   * a user click on "Run analysis". */
  autoFire?: boolean;
  /** Initial collapsed state. Dashboard mounts collapsed; restore-flow
   * mounts expanded so the user sees the result without an extra click. */
  defaultExpanded?: boolean;
}

export function BatchDiagnosisPanel({
  batchId,
  autoFire = false,
  defaultExpanded = false,
}: BatchDiagnosisPanelProps) {
  const ability = useAbility();
  const diagnose = useDiagnoseBatch(batchId);
  const [diagnosis, setDiagnosis] = useState<BatchDiagnosis | null>(null);
  const [expanded, setExpanded] = useState(defaultExpanded);

  const isOperator = ability.can("manage", "all");
  const register: "operator" | "user" = isOperator ? "operator" : "user";

  // refresh=true forces a fresh server-side run ("Re-run analysis"); the
  // initial/auto-fire call omits it so a replay of the cached diagnosis is fine.
  const run = (refresh = false) => {
    diagnose.mutate(
      { refresh },
      {
        onSuccess: (res) => {
          setDiagnosis(res.diagnosis);
          setExpanded(true);
        },
        onError: (e) =>
          toast.error(
            e instanceof Error
              ? e.message
              : "We couldn't analyze this import. Try again shortly.",
          ),
      },
    );
  };

  // Auto-fire on mount in the restore-flow inline mount. Must run from
  // useEffect (not during render) so StrictMode's double-invoke and any
  // concurrent-rendering aborted trees don't double-dispatch the mutation.
  // The mutation is guarded by isIdle inside the effect — once a mutation
  // has been initiated, isIdle stays false for the lifetime of this hook
  // instance, so re-runs of the effect from re-renders are no-ops.
  useEffect(() => {
    if (!autoFire) return;
    if (diagnosis) return;
    if (!diagnose.isIdle) return;
    run();
    // run/diagnose are stable refs from useDiagnoseBatch; depending on
    // batchId + autoFire matches the plan's "on-mount auto-fire" intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, autoFire]);

  return (
    <Card>
      <CardHeader>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-center justify-between gap-4 text-left"
          aria-expanded={expanded}
          aria-controls={`batch-diagnosis-body-${batchId}`}
        >
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2">
              <Sparkles
                className="size-4 text-[color:var(--color-accent)]"
                aria-hidden="true"
              />
              What happened?
            </CardTitle>
            <CardDescription>
              {diagnosis
                ? "Adva grouped the import errors across this batch and identified the root cause."
                : "Run an AI analysis across all the import jobs in this batch to find the root cause."}
            </CardDescription>
          </div>
          <ChevronDown
            className={`size-4 shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>
      </CardHeader>

      {expanded && (
        <CardContent id={`batch-diagnosis-body-${batchId}`}>
          {!diagnosis && !diagnose.isPending && !diagnose.isError && (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-[color:var(--color-text-md)]">
                Aggregates errors from every child import in this batch into a
                single root-cause analysis with one-click next steps.
              </p>
              <Button onClick={() => run()} className="self-start">
                Run analysis
              </Button>
            </div>
          )}

          {diagnose.isPending && (
            <div className="space-y-3" aria-label="Running analysis">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          )}

          {diagnose.isError && !diagnose.isPending && (
            <Alert variant="destructive">
              <AlertTitle>We couldn't analyze this batch</AlertTitle>
              <AlertDescription>
                {diagnose.error instanceof Error
                  ? diagnose.error.message
                  : "The analysis could not be produced. Try again shortly."}
              </AlertDescription>
            </Alert>
          )}

          {diagnosis && !diagnose.isPending && (
            <div className="space-y-4">
              {diagnosis.clusters.length > 0 && (
                <ClusterPills clusters={diagnosis.clusters} />
              )}
              <LadderBreadcrumb blockingTier={diagnosis.blocking_tier} />

              <DiagnosisResult
                diagnosis={asImportDiagnosis(diagnosis)}
                register={register}
                handlers={{
                  // Batch-grain remediation: per-job retry/cancel/file-ticket
                  // don't have a batch equivalent yet. Surface only what we
                  // can act on — additional batch actions land here once their
                  // backends ship (retry_permissive, edit_data — see plan
                  // §Phase 2 "remediation switch extension").
                  canRetry: false,
                  canFileTicket: false,
                }}
              />

              {diagnosis.review_url && (
                <Alert>
                  <AlertTitle>Review required</AlertTitle>
                  <AlertDescription>
                    <Link
                      to={diagnosis.review_url}
                      className="underline underline-offset-2"
                    >
                      One or more imports in this batch are waiting for your
                      review.
                    </Link>
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/import/batches/${batchId}/records?filter=errors`}>
                    Show records with errors
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => run(true)}
                  disabled={diagnose.isPending}
                >
                  {diagnose.isPending ? (
                    <>
                      <Loader2
                        className="size-4 animate-spin"
                        aria-hidden="true"
                      />
                      Re-running…
                    </>
                  ) : (
                    "Re-run analysis"
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
